import { useState, MouseEvent, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { withTranslation, WithTranslation, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import Logo from 'packages/kakapo-ui-kit/components/common/Logo';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import MainNavigationLink from 'packages/kakapo-ui-kit/components/navigation/MainNavigationLink';
import { INavigation } from 'packages/kakapo-ui-kit/types/Navigation';
import RelativeContainer from 'packages/kakapo-ui-kit/components/atoms/RelativeContainer';
import DropdownList, {
	DropdownListItem,
	DropdownListSeperator,
} from 'packages/kakapo-ui-kit/components/lists/DropdownList';
import SearchFieldWrapper from 'packages/kakapo-ui-kit/components/forms/controls/SearchFieldWrapper';
import CreateIcon from 'packages/kakapo-ui-kit/assets/icons/CreateIcon';
import { PrimaryButtonLink } from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import DragHamburgerIcon from 'packages/kakapo-ui-kit/assets/icons/DragHamburgerIcon';
import * as graphql from 'generated/graphql';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import { useMixpanel } from 'packages/kakapo-ui-application/hooks/useMixpanel';

import IntercomTarget from '../../../kakapo-ui-application/common/containers/IntercomTarget';

import TrialExpiryWarning from './TrialExpiryWarning';

const LineGradient = styled.div`
	width: 100%;
	height: 3px;
	background-image: ${(props) => props.theme.images.lineGradient};
`;

const Element = styled.div`
	width: 100%;
	position: fixed;
	z-index: 5;
	background-color: ${(props) => props.theme.colors.white};
	padding: 0 0;
	// Small opacity border underneath the header (overlaps content below)
	box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.08);
`;

const ShadowElement = styled.div`
	width: 100%;
	height: 75px;
`;

interface IProps extends INavigation, WithTranslation {
	user?: graphql.User;
	callToActionButtonLink?: string;
}

interface IProfileNavigationProps {
	user?: graphql.User;
}

const ProfileNavigation = ({ user }: IProfileNavigationProps) => {
	const mixpanel = useMixpanel();

	const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
	const { t } = useTranslation();

	return (
		<RelativeContainer>
			<MainNavigationLink
				icon={<PersonAvatar size={PersonAvatarSize.Small} person={user} />}
				link={{
					title: user?.firstName || '',
					onClick: (event: MouseEvent<HTMLAnchorElement>) => {
						mixpanel?.track('navigation.profile');

						event.preventDefault();
						setIsDropdownVisible(!isDropdownVisible);
					},
				}}
			/>

			{isDropdownVisible && (
				<DropdownList hasNegativeOffsetTop>
					<DropdownListItem>
						<Link to={user ? `/user/${user.id}` : '#'} onClick={() => setIsDropdownVisible(false)}>
							{t('common.profile.title')}
						</Link>
					</DropdownListItem>
					<DropdownListItem>
						<Link
							to={user ? `/user/${user.id}/settings` : '#'}
							onClick={() => setIsDropdownVisible(false)}
						>
							{t('common.settings.title')}
						</Link>
					</DropdownListItem>
					<DropdownListSeperator />
					<DropdownListItem>
						<Link to="/logout" onClick={() => setIsDropdownVisible(false)}>
							{t('common.logout')}
						</Link>
					</DropdownListItem>
				</DropdownList>
			)}
		</RelativeContainer>
	);
};

const SidebarVisibleStyle = createGlobalStyle`
	.sidebar__main {
		display: block !important;
	}

	// .sidebar__main {
	// 	display: block !important;
	// }
`;

const MainNavigationHamburgerButton = styled.button`
	height: 71px;
	padding: 0 20px;
	margin-left: -20px;
	display: flex;
	align-items: center;
	background-color: transparent;

	svg {
		margin-right: 10px;
	}
`;

const MainNavigationBar = ({ t, user, renderMenuItems, callToActionButtonLink }: IProps) => {
	const mixpanel = useMixpanel();

	const [isMobileNavigationVisible, setIsMobileNavigationVisible] = useState<boolean>(false);

	const location = useLocation();
	useEffect(() => {
		setIsMobileNavigationVisible(false);
	}, [location]);

	const workspace = useWorkspace();

	const roles = useViewerRoles();

	const { data } = graphql.useMainNavigationBarQuery({
		variables: {
			id: workspace.id,
		},
	});

	return (
		<>
			<Element>
				<LineGradient />
				{isMobileNavigationVisible && <SidebarVisibleStyle />}

				<Flex mx={3} alignItems="center" flexWrap="wrap" justifyContent="space-between">
					<Box display={['block', 'block', 'none']}>
						<MainNavigationHamburgerButton
							onClick={() => {
								setIsMobileNavigationVisible(!isMobileNavigationVisible);
							}}
							type="button"
						>
							<DragHamburgerIcon />
							Menu
						</MainNavigationHamburgerButton>
					</Box>
					<Flex
						width={['auto', 6 / 12, 6 / 12]}
						justifyContent={['center', 'center', 'flex-start']}
						flexGrow={[1, 0, 0]}
					>
						<Box width={100} flexShrink={1} mr={2}>
							<Logo to="/" />
						</Box>

						<Box flexGrow={1} display={['none', 'none', 'block']}>
							{user && (
								<Box pl={3}>
									<IntercomTarget target="searchbar-with-compliments" block>
										<SearchFieldWrapper />
									</IntercomTarget>
								</Box>
							)}
						</Box>
					</Flex>

					{data?.workspace.trialExpiresAt &&
						new Date(data.workspace.trialExpiresAt) > new Date() &&
						roles.isWorkspaceSuperuser && (
							<Box display={['none', 'none', 'none', 'inline-block']}>
								<TrialExpiryWarning expiryDate={new Date(data.workspace.trialExpiresAt)} />
							</Box>
						)}

					<Flex alignItems="center">
						{user && (
							<>
								{callToActionButtonLink !== undefined && (
									<Column px={0} mr={2} display={['none', 'none', 'inline-block']}>
										<PrimaryButtonLink
											onClick={() => mixpanel?.track('navigation.feedback')}
											to={callToActionButtonLink!}
											size={ButtonSize.Small}
										>
											<CreateIcon />
											{t('common.navigation.askFeedback')}
										</PrimaryButtonLink>
									</Column>
								)}
								{/* Profile button for desktop */}
								<Column px={0}>
									<ProfileNavigation user={user} />
								</Column>
							</>
						)}
					</Flex>
				</Flex>
			</Element>

			<ShadowElement />
		</>
	);
};

export default withTranslation()(MainNavigationBar);
